import { supabase } from "../supabaseClient";

export { EVENT_FEES_LABEL } from "./prizeFundEstimate";

export const sortFees = (fees) =>
  [...(fees || [])].sort((a, b) => {
    if (a.feeName === "Prize Fund") return -1;
    if (b.feeName === "Prize Fund") return 1;
    return (a.feeName || "").localeCompare(b.feeName || "");
  });

export const filledEventFees = (amounts) =>
  Object.entries(amounts || {})
    .filter(([, amount]) => amount !== "" && Number(amount) > 0)
    .map(([feeID, amount]) => ({ feeID, amount: Number(amount) }));

export const amountsFromEvent = (event, catalog) => {
  const next = {};
  (catalog || []).forEach((fee) => {
    const existing = (event?.fees || []).find(
      (row) => row.feeName === fee.feeName
    );
    next[fee.id] = existing != null ? String(existing.amount) : "";
  });
  return next;
};

export const formatEventFeesSummary = (fees) =>
  sortFees(fees || [])
    .filter((fee) => fee.amount != null && Number(fee.amount) > 0)
    .map((fee) => `${fee.feeName} $${Number(fee.amount).toFixed(2)} per bowler`)
    .join(" · ");

export const eventHasFees = (event) =>
  (event?.fees || []).some(
    (fee) => fee.amount != null && Number(fee.amount) > 0
  );

const asFee = (row) => ({
  id: row.id,
  feeName: row.fees?.feeName || row.feeName,
  amount: row.amount,
});

export const syncEventFees = async ({
  event,
  amounts,
  catalog,
  isPayoutFinalized = false,
}) => {
  const isTemplate = event.isTemplate || event.templateId != null;
  const current = [...(event.fees || [])];
  const next = [...current];

  for (const fee of catalog || []) {
    const trimmed = String(amounts?.[fee.id] ?? "").trim();
    const hasAmount = trimmed !== "" && Number(trimmed) > 0;
    const existing = next.find((row) => row.feeName === fee.feeName);

    if (!hasAmount) {
      if (!existing) continue;
      const { data: deletedFee, error } = isTemplate
        ? await supabase
            .from("eventSquadTemplateFees")
            .delete()
            .eq("id", existing.id)
            .select()
        : await supabase.from("eventFee").delete().eq("id", existing.id).select();
      if (error) throw error;
      if (!deletedFee || deletedFee.length === 0) {
        throw new Error(
          isPayoutFinalized
            ? "Cannot delete fee: Payouts have been locked and cannot be modified."
            : "Delete failed: No fee was deleted."
        );
      }
      const index = next.findIndex((row) => row.id === existing.id);
      if (index >= 0) next.splice(index, 1);
      continue;
    }

    const amount = Number(trimmed);
    if (existing) {
      if (Number(existing.amount) === amount) continue;
      const { data: updatedFee, error } = isTemplate
        ? await supabase
            .from("eventSquadTemplateFees")
            .update({ feeID: fee.id, amount })
            .eq("id", existing.id)
            .select("id, amount, fees ( feeName )")
            .single()
        : await supabase
            .from("eventFee")
            .update({ feeID: fee.id, amount })
            .eq("id", existing.id)
            .select("id, amount, fees ( feeName )")
            .single();
      if (error) throw error;
      if (!updatedFee) {
        throw new Error(
          isPayoutFinalized
            ? "Cannot update fee: Payouts have been locked and cannot be modified."
            : "Update failed: No fee was updated."
        );
      }
      const index = next.findIndex((row) => row.id === existing.id);
      if (index >= 0) next[index] = asFee(updatedFee);
      continue;
    }

    const { data: newFee, error } = isTemplate
      ? await supabase
          .from("eventSquadTemplateFees")
          .insert([{ templateID: event.id, feeID: fee.id, amount }])
          .select("id, amount, fees ( feeName )")
          .single()
      : await supabase
          .from("eventFee")
          .insert([{ eventsID: event.id, feeID: fee.id, amount }])
          .select("id, amount, fees ( feeName )")
          .single();
    if (error) throw error;
    if (!newFee) {
      throw new Error(
        isPayoutFinalized
          ? "Cannot add fee: Payouts have been locked and cannot be modified."
          : "Creation failed: No fee was added."
      );
    }
    next.push(asFee(newFee));
  }

  return next;
};
